import React from 'react';
import { Chip, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { ITranslatorService } from '../../../../services/Interfaces/ITranslatorService';
import DamageReferentialLabel from '../../../_shared/DamageReferentialLabel';
import { GTOperationEnum } from '../../../../helpers/Constants';
import {
  PartMappingDamageSeverityDto,
  PartMappingDamageTypeDto
} from '../../../../interfaces/Organization';

interface IPartMappingTreeDamageTypeProps {
  damageType: PartMappingDamageTypeDto;
  translatorService: ITranslatorService;
}

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper
  },
  label: {
    flex: 1
  },
  time: {
    minWidth: theme.spacing(7),
    textAlign: 'right',
    fontWeight: theme.typography.fontWeightBold
  },
  severities: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75),
    paddingLeft: theme.spacing(3)
  },
  sectionTitle: {
    display: 'block',
    fontWeight: theme.typography.fontWeightBold
  }
}));

const PartMappingTreeDamageType: React.FC<IPartMappingTreeDamageTypeProps> = ({
  damageType,
  translatorService
}) => {
  const classes = useStyles();

  const hasSeverities = damageType.damageSeverities.length > 0;
  const isReplacement = damageType.operation?.code === GTOperationEnum.SU;
  const replacementPaintLabel = damageType.hasReplacementPaint
    ? translatorService.Tranlate('PART_MAPPING_REPLACEMENT_PAINT_LABEL', 'Se vopsește la înlocuire')
    : translatorService.Tranlate('PART_MAPPING_NO_REPLACEMENT_PAINT', 'Nu se vopsește la înlocuire');

  const renderTime = (time: number | null) =>
    time != null ? (
      <Typography variant="body2" className={classes.time}>
        {translatorService.Tranlate('PART_MAPPING_TREE_TIME', 'Timp')}: {time}{' '}
        {translatorService.Tranlate('PART_MAPPING_TREE_HOURS', 'h')}
      </Typography>
    ) : null;

  const renderSeverity = (damageSeverity: PartMappingDamageSeverityDto) => (
    <div key={damageSeverity.id} className={classes.card}>
      <div className={classes.label}>
        <DamageReferentialLabel
          displayName={damageSeverity.damageSeverity?.displayName}
          displayDescription={damageSeverity.damageSeverity?.displayDescription}
          level={damageSeverity.damageSeverity?.level}
        />
      </div>
      {renderTime(damageSeverity.time)}
    </div>
  );

  return (
    <div>
      <div className={classes.card}>
        <div className={classes.label}>
          <DamageReferentialLabel
            displayName={damageType.damageType?.displayName}
            displayDescription={damageType.damageType?.displayDescription}
          />
        </div>
        {damageType.operation ? (
          <Chip size="small" color="primary" label={damageType.operation.displayName} />
        ) : null}
        {isReplacement ? <Chip size="small" variant="outlined" label={replacementPaintLabel} /> : null}
        {!hasSeverities ? renderTime(damageType.time) : null}
      </div>
      {hasSeverities ? (
        <div className={classes.severities}>
          <Typography variant="overline" color="primary" className={classes.sectionTitle}>
            {translatorService.Tranlate('PART_MAPPING_TREE_SEVERITIES', 'Severități')}
          </Typography>
          {damageType.damageSeverities.map((damageSeverity) => renderSeverity(damageSeverity))}
        </div>
      ) : null}
    </div>
  );
};

export default PartMappingTreeDamageType;
